import { ExpenseModel, ExpenseType, PaymentType } from 'api/model';
import { useFileUploadFileHook } from 'api/file/file';
import { emptyGuid } from 'constants/AppConstants';
import useForm from 'hooks/useForm';
import { useCallback, useMemo, useState } from 'react';
import { useApiCallFn } from 'hooks/useApiCallFn';

function expenseModelToExpenseForm(expense: ExpenseModel | undefined) {
  if (expense) {
    return {
      expenseType: String(expense.expenseType),
      paymentType: String(expense.paymentType),
      message: expense.message,
      value: String(expense.value),
    };
  }
  return {
    expenseType: '6',
    paymentType: '1',
    message: '',
    value: '0',
  };
}

export function expenseFormToExpenseModel(
  base: ExpenseModel | undefined,
  expenseForm: ReturnType<typeof expenseModelToExpenseForm>
) {
  const model: ExpenseModel = {
    expenseType: Number(expenseForm.expenseType) as ExpenseType,
    paymentType: Number(expenseForm.paymentType) as PaymentType,
    message: expenseForm.message,
    value: Number(expenseForm.value),

    id: base?.id ?? 0,
    assignmentID: base?.assignmentID ?? -1,
    fileGuid: base?.fileGuid ?? emptyGuid,
    fileEnding: base?.fileEnding ?? '',
    created: base?.created ?? new Date(),
    createdByID: base?.createdByID ?? -1,
    createdBy: base?.createdBy ?? '',
    modified: base?.modified ?? new Date(),
    modifiedByID: base?.modifiedByID ?? -1,
    modifiedBy: base?.modifiedBy ?? '',
  };
  return model;
}

const useExpenseEditorForm = (
  expense: ExpenseModel | undefined,
  onSaveExpense: (expense: ExpenseModel) => void,
  initialFile?: File
) => {
  const form = useForm(expenseModelToExpenseForm(expense));
  // if undefined: dont change image, if null: clear image, if File: update image.
  const [selectedFile, setSelectedFile] = useState<File | null | undefined>(
    initialFile ?? undefined
  );

  const uploadFile = useFileUploadFileHook();

  const uploadFileCall = useApiCallFn(
    // fileName is kept in the signature for call-site compatibility but is no
    // longer sent — the Orval upload body only carries imageFile + fileEnding.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (file: File, fileName: string, fileEnding: string) =>
      uploadFile({ imageFile: file, fileEnding })
  );

  const { state } = form;

  const handleSaveClick = useCallback(async () => {
    const updatedModel = expenseFormToExpenseModel(expense, state);

    if (selectedFile) {
      const fileName = selectedFile.name;
      const fileEnding = fileName.match(/\.(\w*)$/)?.[1] ?? '';

      const [fileGuid, error] = await uploadFileCall.run(
        selectedFile,
        fileName,
        fileEnding
      );

      if (fileGuid && !error) {
        updatedModel.fileGuid = fileGuid;
        updatedModel.fileEnding = fileEnding;
      }
    } else if (selectedFile === null) {
      updatedModel.fileGuid = emptyGuid;
      updatedModel.fileEnding = '';
    }

    onSaveExpense(updatedModel);
  }, [expense, onSaveExpense, selectedFile, state, uploadFileCall]);

  return useMemo(
    () => ({
      form,
      onSaveClick: handleSaveClick,
      selectedFile,
      setSelectedFile,
    }),
    [form, handleSaveClick, selectedFile]
  );
};

export default useExpenseEditorForm;
